import { format } from 'date-fns';
import {
  DayOfWeekEnum,
  GenderEnum,
  PlayerFinderTimeOfDayEnum,
  type PlayerProfileFieldsFragment,
  SportsEnum,
} from 'types/generated/client';
import { getVerificationLevel } from 'types/verification';
import { DAYS_OF_WEEK_IN_ORDER, dayOfWeekPluralLabel } from 'utils/shared/dayOfWeek';
import { isUuidFormatValid } from 'utils/shared/string/isUuidFormatValid';
import type {
  PlayerProfileAvailabilitySlot,
  PlayerProfileExperience,
  PlayerProfileSkillLevel,
  PlayerProfileSocialLink,
  PlayerProfileView,
} from './types';

const TIME_OF_DAY_LABELS: Record<PlayerFinderTimeOfDayEnum, string> = {
  [PlayerFinderTimeOfDayEnum.Morning]: 'Mornings',
  [PlayerFinderTimeOfDayEnum.Afternoon]: 'Afternoons',
  [PlayerFinderTimeOfDayEnum.Evening]: 'Evenings',
  [PlayerFinderTimeOfDayEnum.Flexible]: 'Anytime',
};

const WEEKEND_DAYS = [DayOfWeekEnum.Saturday, DayOfWeekEnum.Sunday];

/** Onboarding writes a plain URL or a bare handle; only the former is safe to link. */
const toSocialHref = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const buildSocialLinks = (profile: PlayerProfileFieldsFragment): PlayerProfileSocialLink[] =>
  (
    [
      ['instagram', profile.instagramUrl],
      ['facebook', profile.facebookUrl],
      ['tiktok', profile.tiktokUrl],
      ['linkedin', profile.linkedinUrl],
      ['x', profile.xUrl],
    ] as const
  ).flatMap(([key, value]) => {
    const href = toSocialHref(value);
    return href ? [{ key, href } as PlayerProfileSocialLink] : [];
  });

/**
 * One pill per sport the player listed. The detail is their rating when they gave
 * one ("VAIR 4.0"), otherwise the level they self-rated ("Intermediate"). A sport
 * with neither still shows, so the pill count matches what they picked.
 */
const buildExperiences = (profile: PlayerProfileFieldsFragment): PlayerProfileExperience[] => {
  // Each sport's rating rows share a shape across the three junction tables, so a
  // union of the generated row types keeps this in step with the fragment.
  type RatingRow =
    | PlayerProfileFieldsFragment['pickleballRatingScales'][number]
    | PlayerProfileFieldsFragment['tennisRatingScales'][number]
    | PlayerProfileFieldsFragment['padelRatingScales'][number];

  const ratingsBySport: Partial<Record<SportsEnum, RatingRow[]>> = {
    [SportsEnum.Pickleball]: profile.pickleballRatingScales,
    [SportsEnum.Tennis]: profile.tennisRatingScales,
    [SportsEnum.Padel]: profile.padelRatingScales,
  };

  const skillLevelBySport: Partial<Record<SportsEnum, PlayerProfileSkillLevel>> = {
    [SportsEnum.Pickleball]: profile.pickleballSkillLevel,
    [SportsEnum.Tennis]: profile.tennisSkillLevel,
    [SportsEnum.Padel]: profile.padelSkillLevel,
  };

  return profile.sports.map((row) => {
    const rating = ratingsBySport[row.sport]?.find(
      (entry) => entry.scale?.shortName && entry.rating != null,
    );
    const detail = rating
      ? `${rating.scale.shortName} ${rating.rating}`
      : skillLevelBySport[row.sport]?.displayName ?? '';

    return { key: row.id, sport: row.sport, detail };
  });
};

/**
 * Availability is stored one row per (day, time), because "Weekends" and
 * "I'm flexible" are fanned out to real weekdays on write. This folds them back
 * up so the profile reads the way the player answered rather than as seven rows.
 */
const buildAvailability = (
  profile: PlayerProfileFieldsFragment,
): PlayerProfileAvailabilitySlot[] => {
  const daysByTime = new Map<PlayerFinderTimeOfDayEnum, Set<DayOfWeekEnum>>();

  profile.playerFinderAvailability.forEach(({ dayOfWeek, timeOfDay }) => {
    if (!daysByTime.has(timeOfDay)) daysByTime.set(timeOfDay, new Set());
    daysByTime.get(timeOfDay)?.add(dayOfWeek);
  });

  const slots: PlayerProfileAvailabilitySlot[] = [];

  daysByTime.forEach((days, time) => {
    const timeLabel = TIME_OF_DAY_LABELS[time] ?? '';

    if (DAYS_OF_WEEK_IN_ORDER.every((day) => days.has(day))) {
      slots.push({ key: `${time}-any`, dayLabel: 'Any day', timeLabel });
      return;
    }

    const remaining = new Set(days);
    if (WEEKEND_DAYS.every((day) => remaining.has(day))) {
      slots.push({ key: `${time}-weekends`, dayLabel: 'Weekends', timeLabel });
      WEEKEND_DAYS.forEach((day) => remaining.delete(day));
    }

    DAYS_OF_WEEK_IN_ORDER.filter((day) => remaining.has(day)).forEach((day) => {
      slots.push({ key: `${time}-${day}`, dayLabel: dayOfWeekPluralLabel(day), timeLabel });
    });
  });

  return slots;
};

/** `user_profiles.gender` is a text column, so an unrecognised value reads as none. */
const toGender = (value: string | null | undefined): GenderEnum | null =>
  Object.values(GenderEnum).find((gender) => gender === value) ?? null;

/** "July 8, 1991", or "July 8" when the player chose to hide the year. */
export const formatBirthday = (birthday: PlayerProfileView['birthday']): string => {
  if (!birthday) return '';
  // Year only affects the rendered string when shown, so any leap-safe year works.
  const date = new Date(birthday.year ?? 2000, birthday.month - 1, birthday.day);
  return birthday.year ? format(date, 'MMMM d, yyyy') : format(date, 'MMMM d');
};

export const toPlayerProfileView = (
  profile: PlayerProfileFieldsFragment,
  gamesPlayed: number,
): PlayerProfileView => {
  const hasBirthday = profile.birthdayMonth != null && profile.birthdayDay != null;
  const reviewCount = profile.playerReviewsAggregate.aggregate?.count ?? 0;
  const averageRating = profile.playerReviewsAggregate.aggregate?.avg?.rating ?? null;

  return {
    id: profile.id,
    // A UUID here is the signup placeholder, not a handle the player chose.
    username: profile.username && !isUuidFormatValid(profile.username) ? profile.username : null,
    name: profile.fullName || profile.preferredName || 'Player',
    cityName: profile.cityName || null,
    // The view column is text, so the enum narrowing happens once, here, rather
    // than with a cast at each render site.
    gender: toGender(profile.gender),
    aboutMe: profile.aboutMe || null,
    profileImagePath: profile.profileImagePath,
    coverImagePath: profile.coverImagePath,
    // Verified is the identity row; Trusted additionally needs the review threshold,
    // so this count is what promotes the badge.
    verificationLevel: getVerificationLevel({
      identityVerifications: profile.identityVerifications,
      reviewCount,
    }),
    birthday: hasBirthday
      ? {
          month: profile.birthdayMonth as number,
          day: profile.birthdayDay as number,
          year: profile.birthdayYear ?? null,
        }
      : null,
    socialLinks: buildSocialLinks(profile),
    experiences: buildExperiences(profile),
    availability: buildAvailability(profile),
    venues: profile.venueFollows.flatMap(({ venue }) => (venue ? [venue] : [])),
    photos: profile.userFiles,
    reviews: profile.playerReviews,
    stats: {
      reviewCount,
      averageRating,
      followers: profile.followersAggregate.aggregate?.count ?? 0,
      gamesPlayed,
    },
  };
};
